import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../helpers/methods";
import {
  buildFormShareLinks,
  type FormShareLink,
  type FormSharePlatformId,
} from "../publicFormUrl";

function copyText(text: string, label: string) {
  return navigator.clipboard
    .writeText(text)
    .then(() => openSuccessNotification(`${label} copied`))
    .catch(() => openErrorNotification("Unable to copy link"));
}

function PlatformIcon({
  id,
  color,
  size = 16,
}: {
  id: FormSharePlatformId;
  color: string;
  size?: number;
}) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": true as const,
  };
  switch (id) {
    case "meta":
      return (
        <svg {...common} fill={color}>
          <path d="M22 12.07C22 6.5 17.52 2 12 2S2 6.5 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.03H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.75 8.44-4.91 8.44-9.93z" />
        </svg>
      );
    case "instagram":
      return (
        <svg {...common} fill={color}>
          <path d="M7 3h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7a4 4 0 014-4zm10 2H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2zm-5 3.2A3.8 3.8 0 1112 16.8 3.8 3.8 0 0112 8.2zm0 1.8a2 2 0 100 4 2 2 0 000-4zM17.2 6.4a.9.9 0 11-.001 1.8.9.9 0 01.001-1.8z" />
        </svg>
      );
    case "google":
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M21.6 12.23c0-.74-.07-1.45-.19-2.13H12v4.04h5.4a4.62 4.62 0 01-2 3.03v2.51h3.24c1.9-1.75 3-4.33 3-7.45z"
          />
          <path
            fill="#34A853"
            d="M12 22c2.7 0 4.96-.9 6.62-2.32l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.07v2.59A10 10 0 0012 22z"
          />
          <path
            fill="#FBBC05"
            d="M6.41 13.01A6.01 6.01 0 016.1 12c0-.35.05-.69.1-1.01V8.4H3.07A10 10 0 002 12c0 1.62.39 3.14 1.07 4.5l3.34-3.49z"
          />
          <path
            fill="#EA4335"
            d="M12 5.88c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 2.89 14.7 2 12 2A10 10 0 003.07 8.4l3.34 2.59C7.2 7.63 9.4 5.88 12 5.88z"
          />
        </svg>
      );
    case "youtube":
      return (
        <svg {...common} fill={color}>
          <path d="M23 12.2s0-3.1-.4-4.5c-.2-.8-.9-1.5-1.7-1.7C19.4 5.6 12 5.6 12 5.6s-7.4 0-8.9.4c-.8.2-1.5.9-1.7 1.7C1 9.1 1 12.2 1 12.2s0 3.1.4 4.5c.2.8.9 1.5 1.7 1.7 1.5.4 8.9.4 8.9.4s7.4 0 8.9-.4c.8-.2 1.5-.9 1.7-1.7.4-1.4.4-4.5.4-4.5zM9.8 15.6V8.8l6.2 3.4-6.2 3.4z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg {...common} fill={color}>
          <path d="M4.98 3.5A2.5 2.5 0 112.5 6a2.5 2.5 0 012.48-2.5zM3 8.5h4V21H3zM9 8.5h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.5c0-1.31-.02-3-1.83-3-1.83 0-2.11 1.43-2.11 2.9V21H9z" />
        </svg>
      );
    case "whatsapp":
      return (
        <svg {...common} fill={color}>
          <path d="M20.5 3.5A11 11 0 003.4 18.9L2 22.5l3.7-1.4A11 11 0 1020.5 3.5zM12 20.2a9.1 9.1 0 01-4.64-1.27l-.33-.2-2.2.83.83-2.14-.22-.35A9.15 9.15 0 1112 20.2zm5.28-6.85c-.29-.14-1.7-.84-1.96-.93-.26-.1-.45-.14-.64.14-.19.29-.74.93-.9 1.12-.17.19-.33.21-.62.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.45.13-.59.13-.13.29-.33.43-.5.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.64-1.54-.88-2.1-.23-.55-.47-.48-.64-.49h-.55c-.19 0-.5.07-.76.36-.26.29-1 1-1 2.43s1.03 2.82 1.17 3.02c.14.19 2.03 3.1 4.92 4.35.69.3 1.22.48 1.64.61.69.22 1.31.19 1.8.12.55-.08 1.7-.7 1.94-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.33z" />
        </svg>
      );
    case "twitter":
      return (
        <svg {...common} fill={color}>
          <path d="M14.6 10.4L22.2 2h-1.8l-6.6 7.3L8.6 2H2l8 11.3L2 22h1.8l7-7.7L15.4 22H22l-7.4-11.6zm-2.5 2.7l-.8-1.1L4.5 3.3h2.8l5.2 7.2.8 1.1 6.8 9.4h-2.8l-5.4-7.9z" />
        </svg>
      );
    default:
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          aria-hidden
        >
          <path d="M10 13a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07L10 5.93" />
          <path d="M14 11a5 5 0 00-7.07 0L5.52 12.4a5 5 0 007.07 7.07L14 18.07" />
        </svg>
      );
  }
}

export function FormShareLinkList({
  form,
  compact = false,
}: {
  form: any;
  compact?: boolean;
}) {
  const links = useMemo(() => buildFormShareLinks(form || {}), [form]);
  if (!links.length) {
    return (
      <p className="text-sm text-[#9CA3AF]">Save the form to generate links.</p>
    );
  }

  return (
    <div className={compact ? "space-y-2" : "space-y-2.5"}>
      {links.map((item) => (
        <div
          key={item.id}
          className="flex items-start gap-3 rounded-xl border border-[#EEF0F5] bg-white px-3 py-2.5"
        >
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${item.color}14` }}
          >
            <PlatformIcon id={item.id} color={item.color} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#111827]">{item.label}</p>
            <p className="text-[11px] leading-snug text-[#6B7280]">{item.hint}</p>
            <p className="mt-1 truncate font-mono text-[11px] text-[#4B5563]" title={item.url}>
              {item.url}
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg bg-[#1E3A5F] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#152C49]"
            onClick={() => copyText(item.url, item.label)}
          >
            Copy
          </button>
        </div>
      ))}
    </div>
  );
}

const TABLE_ICON_IDS: FormSharePlatformId[] = [
  "public",
  "meta",
  "instagram",
  "google",
  "whatsapp",
  "linkedin",
];

export function FormShareIconBar({
  form,
  onOpenAll,
}: {
  form: any;
  onOpenAll?: () => void;
}) {
  const links = useMemo(() => buildFormShareLinks(form || {}), [form]);
  const byId = useMemo(() => {
    const map = new Map<string, FormShareLink>();
    links.forEach((l) => map.set(l.id, l));
    return map;
  }, [links]);

  if (!links.length) {
    return <span className="text-sm text-[#9CA3AF]">—</span>;
  }

  return (
    <div className="flex items-center gap-1">
      {TABLE_ICON_IDS.map((id) => {
        const item = byId.get(id);
        if (!item) return null;
        return (
          <button
            key={id}
            type="button"
            data-action-button="true"
            title={`Copy ${item.label}`}
            className="action-button flex h-8 w-8 items-center justify-center rounded-lg border border-[#EEF0F5] bg-white hover:bg-[#F8FAFC]"
            onClick={(e) => {
              e.stopPropagation();
              copyText(item.url, item.label);
            }}
          >
            <PlatformIcon id={item.id} color={item.color} size={15} />
          </button>
        );
      })}
      {onOpenAll ? (
        <button
          type="button"
          data-action-button="true"
          title="All platform links"
          className="action-button ml-0.5 rounded-lg px-2 py-1.5 text-[11px] font-semibold text-[#1E3A5F] hover:bg-[#F3F4F6]"
          onClick={(e) => {
            e.stopPropagation();
            onOpenAll();
          }}
        >
          All
        </button>
      ) : null}
    </div>
  );
}

export default function FormShareLinksPopup({
  open,
  form,
  onClose,
}: {
  open: boolean;
  form: any;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !form || typeof document === "undefined") return null;
  const formName = form.form_name || "Form";
  const kind =
    form.formType === "registration"
      ? "Registration"
      : form.formType === "feedback"
        ? "Feedback"
        : "Form";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      style={{ margin: 0 }}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="relative z-[10000] flex max-h-[min(90vh,720px)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-share-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#F3F4F6] px-5 py-4">
          <div className="min-w-0">
            <h2
              id="form-share-title"
              className="truncate text-base font-semibold text-[#111827]"
            >
              Copy links — {formName}
            </h2>
            <p className="mt-0.5 text-xs text-[#6B7280]">
              {kind} public URL plus tagged links for Meta and other platforms.
              CRM reads <code className="rounded bg-[#F3F4F6] px-1">src</code>{" "}
              and UTM to attribute the response.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg px-2 py-1 text-sm text-[#6B7280] hover:bg-[#F3F4F6]"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FAFC] px-5 py-4">
          <FormShareLinkList form={form} />
        </div>
      </div>
    </div>,
    document.body
  );
}
